import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggerService } from './logger.service';
import { extractRequestIdFromHttpError } from './http-error';

export interface AppErrorRecord {
  type: 'runtime' | 'http';
  message: string;
  timestamp: string;
  status?: number;
  requestId?: string;
  path?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppErrorService {
  private lastError: AppErrorRecord | null = null;

  constructor(private logger: LoggerService) {}

  reportRuntime(error: unknown, path?: string): AppErrorRecord {
    const normalized = this.normalizeRuntimeError(error, path);
    this.lastError = normalized;
    this.logger.error('Unhandled runtime error', normalized);
    return normalized;
  }

  reportHttp(error: HttpErrorResponse, path?: string): AppErrorRecord {
    const normalized: AppErrorRecord = {
      type: 'http',
      message: error.message || 'HTTP error',
      timestamp: new Date().toISOString(),
      status: error.status,
      requestId: extractRequestIdFromHttpError(error) || undefined,
      path: path || error.url || undefined,
    };
    this.lastError = normalized;
    this.logger.error('Unhandled HTTP error', normalized);
    return normalized;
  }

  getLastError(): AppErrorRecord | null {
    return this.lastError;
  }

  private normalizeRuntimeError(error: unknown, path?: string): AppErrorRecord {
    if (error instanceof HttpErrorResponse) {
      return {
        type: 'http',
        message: error.message || 'HTTP error',
        timestamp: new Date().toISOString(),
        status: error.status,
        requestId: extractRequestIdFromHttpError(error) || undefined,
        path: path || error.url || undefined,
      };
    }

    if (error instanceof Error) {
      return {
        type: 'runtime',
        message: error.message || 'Unexpected runtime error',
        timestamp: new Date().toISOString(),
        path,
      };
    }

    return {
      type: 'runtime',
      message: typeof error === 'string' ? error : 'Unexpected runtime error',
      timestamp: new Date().toISOString(),
      path,
    };
  }
}
